import { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, Info, TriangleAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { type InterfaceTraffic, useDeviceTrafficSeries } from '@/hooks/useDeviceTrafficSeries';
import { type LinkEnd, type LinkVerdict, useLinkTrafficVerdict } from '@/hooks/useDeviceTrafficSummary';
import type { Incident } from '@/hooks/useIncidents';
import { useAuth } from '@/hooks/useAuth';
import { useIncidents } from '@/hooks/useIncidents';
import { buildRows, hasQuantity, summariseCapacity, type TrafficRow } from './rows';
import { denominatorNote } from './capacity';
import { formatBucket } from './format';
import { RangeControl } from './RangeControl';
import { RANGE_MS, refreshMs, type TrafficRange } from './range';
import { VerdictStrip } from './VerdictStrip';
import { TrafficMirror } from './TrafficMirror';
import { FaultFacets } from './FaultFacets';
import { EventRail } from './EventRail';

/**
 * One crosshair across *both* columns, which is the point of drawing them side
 * by side: a reader can see that a discard burst at one end sits on the other
 * end's traffic knee, rather than eyeballing two charts.
 */
const SYNC_ID = 'link-traffic';

interface Column {
  end: LinkEnd;
  iface: InterfaceTraffic | undefined;
  rows: TrafficRow[];
  bucketSeconds: number | null;
  bucketFunction: string | null;
}

/**
 * What a wire is doing, from both of its ends.
 *
 * The two-ended sentence at the top is the thing this surface exists for: a
 * link has two independently-polled ends, and errors at one of them with
 * nothing failing at the other localises a fault to a direction, an optic, or
 * one side's cabling. No panel about a single port can say that.
 *
 * The charts are the same blocks the interface panel uses, at the same sizes,
 * fetched per end from the series endpoint narrowed to that port. Only the
 * sentence needs both ends at once.
 */
export function LinkTrafficSection({
  connectionId,
  range: controlledRange,
  onRangeChange,
}: {
  connectionId: string;
  range?: TrafficRange;
  onRangeChange?: (range: TrafficRange) => void;
}) {
  const [localRange, setLocalRange] = useState<TrafficRange>('1h');
  const range = controlledRange ?? localRange;
  const setRange = onRangeChange ?? setLocalRange;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => { setNow(Date.now()); }, refreshMs(range));
    return () => { clearInterval(id); };
  }, [range]);
  const bounds = useMemo(() => ({ start: now - RANGE_MS[range], end: now }), [now, range]);

  const { data: verdict, isLoading, isError } = useLinkTrafficVerdict(connectionId, bounds);

  // Two fixed calls rather than a loop: hooks cannot be conditional, and a link
  // has at most two ends. A missing end passes `undefined` and never fetches.
  const first = useDeviceTrafficSeries(verdict?.ends[0]?.deviceId, bounds, verdict?.ends[0]?.interfaceId);
  const second = useDeviceTrafficSeries(verdict?.ends[1]?.deviceId, bounds, verdict?.ends[1]?.interfaceId);

  const { user } = useAuth();
  const firstDevice = verdict?.ends[0]?.deviceId;
  const secondDevice = verdict?.ends[1]?.deviceId;
  const firstIncidents = useIncidents(user?.organizationId ?? undefined, { deviceId: firstDevice });
  const secondIncidents = useIncidents(user?.organizationId ?? undefined, { deviceId: secondDevice });

  const columns: Column[] = useMemo(() => {
    if (!verdict) return [];
    return verdict.ends.map((end, index) => {
      const response = index === 0 ? first.data : second.data;
      const iface = response?.interfaces.find((row) => row.interfaceId === end.interfaceId);
      return {
        end,
        iface,
        rows: iface ? buildRows(iface) : [],
        bucketSeconds: response?.bucketSeconds ?? null,
        bucketFunction: response?.bucketFunction ?? null,
      };
    });
  }, [verdict, first.data, second.data]);

  // One y-scale across both ends, because the whole reason to put two ends side
  // by side is that they can then be compared by height. Two independently
  // scaled mirrors draw a 40 Mb/s port and a 4 Mb/s port the same size, which
  // is worse than not showing them together at all.
  const shared = useMemo(() => {
    const peak = (keys: (keyof TrafficRow)[]) => {
      const values = columns.flatMap((column) =>
        column.rows.flatMap((row) =>
          keys.flatMap((key) => {
            const value = row[key];
            return typeof value === 'number' ? [Math.abs(value)] : [];
          }),
        ),
      );
      return values.length > 0 ? Math.max(...values) : 0;
    };
    const bits = peak(['bitsIn', 'bitsOut']);
    const errors = peak(['errorsIn', 'errorsOut']);
    const discards = peak(['discardsIn', 'discardsOut']);
    return {
      bits: bits > 0 ? bits * 1.15 : undefined,
      errors: errors > 0 ? errors * 1.2 : undefined,
      discards: discards > 0 ? discards * 1.2 : undefined,
    };
  }, [columns]);

  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <header className="mb-1 flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Traffic across this link
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-[11px] text-muted-foreground">above the line: into that port · below: out of it</p>
          <RangeControl value={range} onChange={setRange} />
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-3 mt-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : isError || verdict === undefined ? (
        <p className="mt-3 text-sm text-muted-foreground">
          The metric store did not answer. The link itself is unaffected.
        </p>
      ) : verdict === null ? (
        <p className="mt-3 text-sm text-muted-foreground">
          There is no traffic to show for this link. It may have changed since the map loaded, or neither end is
          monitored.
        </p>
      ) : (
        <>
          {/* With one end the two-ended table degrades to the single-port one,
              so this sentence and the strip's below it are the same words. The
              strip keeps it, because there it sits with the numbers it is
              about. */}
          {verdict.ends.length > 1 ? (
            <p className="mt-3 flex items-start gap-2 text-sm text-foreground">
              <VerdictIcon severity={verdict.severity} />
              <span>{verdict.sentence}</span>
            </p>
          ) : null}

          {verdict.unmonitoredEnd !== null ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {verdict.unmonitoredEnd} is not monitored, so this is one end&apos;s view of the link. Whether a fault is
              one-sided — the question two ends exist to answer — cannot be settled from here.
            </p>
          ) : null}

          {/* Demoted on purpose: muted, small, under the verdict. It is real
              when it fires and it is narrow — discards are already inside both
              ends' octet counters, so what is left is frames that left one end
              and never physically arrived at the other. */}
          {/* Truthiness, not `!== null`: a backend that predates this field sends
              no key at all, and `undefined !== null` would render an empty
              paragraph for the length of the deploy skew. */}
          {verdict.unaccounted ? (
            <p className="mt-1.5 text-xs text-muted-foreground">{verdict.unaccounted}</p>
          ) : null}

          <div className={columns.length > 1 ? 'mt-5 grid gap-6 lg:grid-cols-2' : 'mt-5'}>
            {columns.map((column, index) => (
              <EndColumn
                key={column.end.deviceId + column.end.interfaceId}
                column={column}
                start={bounds.start}
                end={bounds.end}
                shared={shared}
                incidents={(index === 0 ? firstIncidents.data : secondIncidents.data) ?? []}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function EndColumn({
  column,
  start,
  end,
  shared,
  incidents,
}: {
  column: Column;
  start: number;
  end: number;
  shared: { bits?: number; errors?: number; discards?: number };
  incidents: Incident[];
}) {
  const capacity = summariseCapacity(column.iface?.capacity ?? []);
  // No interface type is available on this surface, so the loopback rule cannot
  // apply here — and does not need to: a loopback is never one end of a
  // discovered link.
  const note = denominatorNote(capacity, null);
  const caption = [
    column.bucketSeconds !== null && column.bucketFunction !== null
      ? `Each point is the ${column.bucketFunction === 'min' ? 'lowest' : 'highest'} reading in ${formatBucket(column.bucketSeconds)}.`
      : null,
    note.caption,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');

  return (
    <div>
      <h3 className="text-xs font-semibold mb-3">
        {column.end.deviceName}{' '}
        <span className="text-muted-foreground font-normal">
          {column.end.interfaceName}
        </span>
      </h3>

      {/* Topology named one row and these numbers came from another. That
          happens on every Juniper link — LLDP reports the logical unit because
          the unit runs the protocol, while the wire's bytes are on the base
          beneath it — and swapping the row without saying so would be the same
          kind of quiet mismatch this resolution exists to fix. */}
      {column.end.portName ? (
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">
          Discovered as {column.end.portName}; counted on{' '}
          {column.end.interfaceName}, which carries the wire.
        </p>
      ) : null}

      <VerdictStrip verdict={column.end.verdict} />

      {!column.iface ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No readings arrived for this port in this window.
        </p>
      ) : (
        <div className="mt-5 space-y-5">
          <TrafficMirror
            rows={column.rows}
            start={start}
            end={end}
            syncId={SYNC_ID}
            capacity={capacity}
            caption={caption}
            mode="bits"
            boundOverride={shared.bits}
          />
          <EventRail
            start={start}
            end={end}
            syncId={SYNC_ID}
            statusBefore={column.iface.statusBefore}
            transitions={column.iface.transitions}
            incidents={incidents}
          />
          <FaultFacets
            rows={column.rows}
            start={start}
            end={end}
            syncId={SYNC_ID}
            hasErrors={hasQuantity(column.iface, 'errors_in') || hasQuantity(column.iface, 'errors_out')}
            hasDiscards={hasQuantity(column.iface, 'discards_in') || hasQuantity(column.iface, 'discards_out')}
            hasPacketMix={hasQuantity(column.iface, 'nonunicast_in') && hasQuantity(column.iface, 'packets_in')}
            boundOverrides={{ errors: shared.errors, discards: shared.discards }}
          />
        </div>
      )}
    </div>
  );
}

function VerdictIcon({ severity }: { severity: LinkVerdict['severity'] }) {
  switch (severity) {
    case 'critical':
      return <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-sev-1" aria-label="critical" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-sev-3" aria-label="warning" />;
    default:
      return <Info className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" aria-label="note" />;
  }
}
